import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Alert,
  Tag,
  Card,
  Badge,
  Divider
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

const EnquiriesManagement = ({ serviceType }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const serviceTypes = [
    { value: 'partner', label: 'Partners Enquiries' },
    { value: 'service', label: 'Services Enquiries' },
    { value: 'skilled', label: 'Skilled Enquiries' },
    { value: 'franchisee', label: 'Franchisee Enquiries' },
    { value: 'business', label: 'Business Enquiries' },
    { value: 'third_party', label: 'Third Party Enquiries' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (serviceType) params.service_type = serviceType;
      
      const { data } = await api.get('/enquiries', { params });
      setEnquiries(data);
    } catch (err) {
      setErrorMsg('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, [serviceType]);

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setVisible(true);
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      ...record,
      follow_up_date: record.follow_up_date ? dayjs(record.follow_up_date) : null
    });
    setEditingId(record.id);
    setVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete this enquiry?',
      content: 'This action cannot be undone.',
      onOk: async () => {
        try {
          await api.delete(`/enquiries/${id}`);
          setSuccessMsg('Enquiry deleted successfully');
          fetchEnquiries();
        } catch {
          setErrorMsg('Failed to delete enquiry');
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        follow_up_date: values.follow_up_date ? values.follow_up_date.format('YYYY-MM-DD HH:mm:ss') : null
      };

      if (editingId) {
        await api.put(`/enquiries/${editingId}`, payload);
        setSuccessMsg('Enquiry updated successfully');
      } else {
        await api.post('/enquiries', payload);
        setSuccessMsg('Enquiry created successfully');
      }
      
      setVisible(false);
      fetchEnquiries();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to save enquiry');
    }
  };

  const getServiceTypeLabel = (type) => {
    const found = serviceTypes.find(st => st.value === type);
    return found ? found.label : type;
  };

  const getStatusTag = (status) => {
    const color = {
      pending: 'orange',
      in_progress: 'blue',
      completed: 'green',
      rejected: 'red'
    }[status];
    return <Tag color={color}>{status.replace('_', ' ').toUpperCase()}</Tag>;
  };

  const columns = [
{
  title: 'S.No.',
  key: 'sno',
  width: 80,
  render: (_text, _record, index) => index + 1,
},
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <small>{record.company_name}</small>
        </div>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.email}</div>
          <div>{record.phone_number}</div>
        </div>
      ),
    },
    {
      title: 'Service Type',
      dataIndex: 'service_type',
      render: (text) => getServiceTypeLabel(text),
    },
    {
      title: 'Date',
      dataIndex: 'date_of_inquiry',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title={`${serviceType ? getServiceTypeLabel(serviceType) : 'All'} Enquiries`}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Enquiry
        </Button>
      }
    >
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          closable
          onClose={() => setSuccessMsg('')}
          style={{ marginBottom: 16 }}
        />
      )}

      {errorMsg && (
        <Alert
          message={errorMsg}
          type="error"
          showIcon
          closable
          onClose={() => setErrorMsg('')}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={enquiries}
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <Modal
        visible={visible}
        title={editingId ? 'Edit Enquiry' : 'Add Enquiry'}
        onCancel={() => setVisible(false)}
        onOk={handleSubmit}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter valid email' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone_number"
              rules={[{ required: true, message: 'Please enter phone number' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Company Name"
              name="company_name"
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Service Type"
              name="service_type"
              rules={[{ required: true, message: 'Please select service type' }]}
            >
              <Select>
                {serviceTypes.map(st => (
                  <Option key={st.value} value={st.value}>{st.label}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                {statusOptions.map(so => (
                  <Option key={so.value} value={so.value}>{so.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Divider orientation="left">Address Details</Divider>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item label="Address" name="address">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item label="City" name="city">
              <Input />
            </Form.Item>

            <Form.Item label="State" name="state">
              <Input />
            </Form.Item>

            <Form.Item label="Country" name="country">
              <Input />
            </Form.Item>

            <Form.Item label="Postal Code" name="postal_code">
              <Input />
            </Form.Item>

            <Form.Item label="Follow Up Date" name="follow_up_date">
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item label="Additional Notes" name="additional_notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default EnquiriesManagement;